import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TemporaryPasswordResetPage from './pages/TemporaryPasswordResetPage';
import TrucksListPage from './pages/TrucksListPage';
import TruckDetailPage from './pages/TruckDetailPage';
import SafetyPage from './pages/SafetyPage';
import FormsPage from './pages/FormsPage';
import ReferralPage from './pages/ReferralPage';
import AdminPage from './pages/AdminPage';
import RecruitmentPage from './pages/RecruitmentPage';
import QuoteManagementPage from './pages/QuoteManagementPage';
import ServicePage from './pages/ServicePage';
import ServiceDashboardPage from './pages/ServiceDashboardPage';
import FormsManagementPage from './pages/FormsManagementPage';
import NeedsALookPage from './pages/NeedsALookPage';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackDetailPage from './pages/FeedbackDetailPage';
import CustomerList from './components/CustomerList';
import CompanyManagement from './components/admin/CompanyManagement';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import { companyUserHome } from './utils/phoneHome';
import { VehicleTypeProvider } from './components/VehicleTypeSelector';

// Import audit service
import auditService from './services/auditService';
import UsageTracker from './components/UsageTracker';

// Component to handle audit service initialization
function AuditInitializer() {
  const { user } = useAuth();
  
  useEffect(() => {
    try {
      if (user) {
        // Set user in audit service when user logs in
        auditService.setUser(user);
        auditService.enable();
      } else {
        // Disable tracking when user logs out
        auditService.disable();
      }
    } catch (error) {
      console.warn('[AuditService] Initialization error (non-fatal):', error.message);
      // Continue without audit service if there's an error
    }
  }, [user]);
  
  return user ? <UsageTracker /> : null;
}

function DashboardEntry() {
  const { user, isLoading } = useAuth();
  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (user.role === 'service_admin' || user.role === 'service_viewer') {
    return <ServiceDashboardPage />;
  }
  return <DashboardPage />;
}

function HomeRedirect() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <Navigate to={companyUserHome(user?.role, isPhone)} replace />;
}

function App() {
  return (
    <VehicleTypeProvider>
      <AuditInitializer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/temporary-password-reset" element={<TemporaryPasswordResetPage />} />
        
        {/* Protected routes with layout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          } 
        >
          <Route index element={<HomeRedirect />} />
          <Route path="dashboard" element={<DashboardEntry />} />
          <Route path="shop" element={
            <ProtectedRoute allowedRoles={['admin', 'company_admin', 'service_admin', 'service_viewer']}>
              <ServicePage />
            </ProtectedRoute>
          } />
          <Route path="trucks" element={<TrucksListPage />} />
          <Route path="truck-detail/:id" element={<TruckDetailPage />} />
          <Route path="safety/*" element={<SafetyPage />} />
          <Route path="forms/*" element={<FormsPage />} />
          <Route path="referrals" element={
            <ProtectedRoute allowedRoles={['company_user', 'company_admin', 'admin']}>
              <ReferralPage />
            </ProtectedRoute>
          } />
          <Route path="feedback/*" element={<FeedbackPage />} />
          <Route path="feedback/view/:id" element={<FeedbackDetailPage />} />
          <Route path="recruitment" element={
            <ProtectedRoute allowedRoles={['admin','company_admin']}>
              <RecruitmentPage />
            </ProtectedRoute>
          } />
          <Route path="quotes" element={
            <ProtectedRoute allowedRoles={['admin','company_admin']}>
              <QuoteManagementPage />
            </ProtectedRoute>
          } />
          <Route path="forms-management" element={
            <ProtectedRoute allowedRoles={['admin','company_admin']}>
              <FormsManagementPage />
            </ProtectedRoute>
          } />
          <Route path="needs-a-look" element={
            <ProtectedRoute allowedRoles={['admin','company_admin']}>
              <NeedsALookPage />
            </ProtectedRoute>
          } />
          <Route path="admin" element={
            <ProtectedRoute allowedRoles={['admin','company_admin']}>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="customers" element={<CustomerList />} />
        </Route>
        
        {/* Fallback route */}
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </VehicleTypeProvider>
  );
}

export default App;
